// src/layouts/MainLayout.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { useMobileMode } from "../../context/MobileModeContext";
import {
  FaBars,
  FaTimes,
  FaChevronDown,
  FaFireAlt,
  FaChartLine,
  FaWallet,
  FaSearch,
  FaCompass,
  FaBolt,
  FaListUl,
  FaUser,
  FaStar,
  FaBell,
  FaTh,
} from "react-icons/fa";
import AuthModal from "../../components/auth/AuthModal";
import { useAuth } from "../../context/AuthContext";
import { FaUsers } from "react-icons/fa";


const SIDEBAR_KEY = "sidebarCollapsed";
const HISTORY_KEY = "recentSearches";
const DEFAULT_TOPBAR_MENUS_HIDDEN = true;

/* =========================================
   🎛️ SIZE CONTROLS (easy to tweak)
   ========================================= */
const MOBILE_ICON_CLASS = "text-2xl";
const MOBILE_HOME_LOGO_PX = 35;

const RAIL_TILE_WH = "w-11 h-11";
const RAIL_ICON_CLASS = "text-lg";
const RAIL_LABEL_CLASS = "text-[11px]";

/* ---------- address heuristics for routing ---------- */
const isSolAddr = (s = "") =>
  /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(String(s).trim());
const isEvmAddr = (s = "") => /^0x[a-fA-F0-9]{40}$/.test(String(s).trim());

// TON: friendly base64url (EQ...) or raw `workchain:hex`
const isTonAddr = (s = "") => {
  const x = String(s).trim();
  if (/^[A-Za-z0-9_-]{48,50}$/.test(x)) return true;
  if (/^[\-]?\d:[a-fA-F0-9]{64}$/.test(x)) return true;
  if (/^[a-fA-F0-9]{64}$/.test(x)) return true;
  return false;
};
const isTronAddr = (s = "") =>
  /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(String(s).trim());
const isXrpAddr = (s = "") =>
  /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(String(s).trim());
const isCardanoAddr = (s = "") => /^addr1[0-9a-z]+$/.test(String(s).trim());
const isFilecoinAddr = (s = "") => /^f[0-9][a-z0-9]+$/i.test(String(s).trim());
const isPolkadotAddr = (s = "") =>
  /^1[1-9A-HJ-NP-Za-km-z]{47,48}$/.test(String(s).trim());
// Sui/Aptos heuristic: long 0x… or type with '::'
const isMoveLike = (s = "") =>
  /^0x[a-fA-F0-9]{64,}$/.test(String(s).trim()) || /::/.test(String(s));

const MainLayout = ({ children }) => {
  /* ---------- Mobile drawers & search ---------- */
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  /* ---------- Force collapsed rail only ---------- */
  const [sidebarCollapsed] = useState(true);
  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, JSON.stringify(true));
  }, []);

  const [topbarMenusHidden] = useState(DEFAULT_TOPBAR_MENUS_HIDDEN);

  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState(
    JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]")
  );

  // NEW: desktop rail drawers
  const [openRailLaunchpad, setOpenRailLaunchpad] = useState(false);
  const [openRailMore, setOpenRailMore] = useState(false);

  const [showAuth, setShowAuth] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const userDisplay = user?.displayName || user?.username || user?.email;

  // Mobile Chart Button (mode) + location
  const { mobileMode, setMobileMode } = useMobileMode();
  const location = useLocation();
  const pathname = location?.pathname || "/";

  // Detect token pages: /{chain}/{address}
  const CHAIN_PATHS = [
    "ethereum","bsc","polygon","arbitrum","base","avalanche","optimism","fantom",
    "solana","ton","tron","xrp","cardano","filecoin","polkadot","aptos","sui"
  ];
  const isTokenPage = React.useMemo(() => {
    for (const c of CHAIN_PATHS) {
      if (pathname.startsWith(`/${c}/`)) {
        const parts = pathname.split("/"); // ["", chain, address, ...]
        return parts.length >= 3 && !!parts[2];
      }
    }
    return false;
  }, [pathname]);

  // --- Menus shared by Sidebar & Top Bar ---
  const mainNavItems = [
    { to: "/trending", label: "Trending", icon: FaFireAlt },
    { to: "/fearandgreedindex", label: "FnG", icon: FaChartLine },
        { to: "/heatmap", label: "Market Heatmap", icon: FaListUl },

    { to: "/intel", label: "Assets", icon: FaWallet },
    { to: "/explorer", label: "Explorer", icon: FaCompass },
    { to: "/address", label: "Address", icon: FaUser },
    { to: "/about", label: "About", icon: FaListUl },
    { to: "/news", label: "News", icon: FaListUl },
    { to: "/top-performance", label: "Top Performance", icon: FaListUl },



  ];
  const launchpadNavItems = [
    { to: "/pumpfun", label: "Pump.fun", icon: FaBolt },
    { to: "/bonkfun", label: "letsBonk", icon: FaBolt },
    { to: "/fourmeme", label: "fourmeme", icon: FaBolt },
  ];
  const moreNavItems = [
     { to: "/bsc-kol", label: "BSC KOL", icon: FaUsers }, 
    { to: "/evm-whales", label: "EVM Whales", icon: FaListUl },
    { to: "/solana-whales", label: "Solana Whales", icon: FaListUl },
    { to: "/usdt-mints", label: "USDT Mints", icon: FaListUl },
    { to: "/usdc-mints", label: "USDC Mints", icon: FaListUl },
    { to: "/ai-analyzer", label: "Analyzer", icon: FaListUl },
    { to: "/meme", label: "Memecoin", icon: FaListUl },
  ];

  /* ---------- 🔎 Global search: route to /{chain}/{address} ---------- */
  const handleGlobalSearch = () => {
    const input = searchValue.trim();
    if (!input) return;

    const updated = [input, ...recentSearches.filter((s) => s !== input)].slice(
      0,
      5
    );
    setRecentSearches(updated);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));

    // ENS / SNS still go to /address
    if (input.endsWith(".eth") || input.endsWith(".sol")) {
      navigate(`/address/${input}`);
    } else if (isEvmAddr(input)) {
      navigate(`/ethereum/${input}`);
    } else if (isSolAddr(input)) {
      navigate(`/solana/${input}`);
    } else if (isTonAddr(input)) {
      navigate(`/ton/${input}`);
    } else if (isTronAddr(input)) {
      navigate(`/tron/${input}`);
    } else if (isXrpAddr(input)) {
      navigate(`/xrp/${input}`);
    } else if (isCardanoAddr(input)) {
      navigate(`/cardano/${input}`);
    } else if (isFilecoinAddr(input)) {
      navigate(`/filecoin/${input}`);
    } else if (isPolkadotAddr(input)) {
      navigate(`/polkadot/${input}`);
    } else if (isMoveLike(input)) {
      // If it's a module type (contains ::), prefer aptos; otherwise try sui
      navigate(`${input.includes("::") ? "/aptos/" : "/sui/"}${input}`);
    } else {
      // fallback to search page
      navigate(`/search?q=${encodeURIComponent(input)}`);
    }

    setSearchValue("");
    setSuggestions([]);
    setMobileSearchOpen(false);
    setMobileOpen(false);
  };

  /* ========= ave.ai rail item ========= */
  const RailItem = ({ to, label, icon: Icon }) => (
    <NavLink to={to} title={label} className="block">
      {({ isActive }) => (
        <div className="flex flex-col gap-1 justify-center items-center py-2">
          <div
            className={[
              "grid place-items-center rounded-2xl transition-colors duration-150",
              RAIL_TILE_WH,
              isActive
                ? "bg-white/[0.14] ring-1 ring-white/25"
                : "bg-white/[0.06] hover:bg-white/[0.10] hover:ring-1 hover:ring-white/20",
            ].join(" ")}
          >
            <Icon
              className={[
                "transition-colors duration-150",
                RAIL_ICON_CLASS,
                isActive ? "text-white" : "text-white/70 hover:text-white",
              ].join(" ")}
            />
          </div>
          <div
            className={[
              "leading-4 text-center w-14 truncate",
              RAIL_LABEL_CLASS,
              isActive ? "text-white font-medium" : "text-white/70 group-hover:text-white",
            ].join(" ")}
          >
            {label}
          </div>
        </div>
      )}
    </NavLink>
  );

  // Non-link rail button (for drawers)
  const RailButton = ({ label, Icon, onClick }) => (
    <button onClick={onClick} title={label} className="w-full">
      <div className="flex flex-col gap-1 justify-center items-center py-2">
        <div
          className={[
            "grid place-items-center rounded-2xl transition-colors",
            RAIL_TILE_WH,
            "bg-white/[0.06] hover:bg-white/[0.10]",
          ].join(" ")}
        >
          <Icon className={`${RAIL_ICON_CLASS} text-white/80`} />
        </div>
        <div className={`${RAIL_LABEL_CLASS} text-white/70`}>{label}</div>
      </div>
    </button>
  );

  const RailSection = ({ items }) => (
    <div className="space-y-1">
      {items.map((item) => (
        <RailItem key={item.to} {...item} />
      ))}
    </div>
  );

  // Drawer tile (mobile + desktop drawers)
  const DrawerTile = ({ to, label, Icon, onClick }) => (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "flex flex-col items-center justify-center rounded-xl border",
          "p-3 transition-colors",
          isActive
            ? "bg-white/10 border-white/20"
            : "bg-white/[0.04] border-white/10 hover:bg-white/10",
        ].join(" ")
      }
    >
      <div className="flex justify-center items-center mb-2 w-12 h-12 rounded-lg ring-1 bg-white/10 ring-white/10">
        <Icon className="text-2xl text-white" />
      </div>
      <div className="text-xs text-center text-white/90">{label}</div>
    </NavLink>
  );

  /* --------------------- DESKTOP RAIL DRAWERS --------------------- */
  const RailDrawer = ({ open, title, onClose, children }) => {
    if (!open) return null;
    return (
      <div className="hidden lg:block">
        {/* scrim */}
        <div className="fixed inset-0 z-[52] bg-black/40" onClick={onClose} />
        {/* left sheet next to rail */}
        <div
          className="fixed z-[53] left-16 top-0 bottom-0 w-[320px] bg-black border-r border-white/10 shadow-2xl"
          role="dialog"
          aria-label={title}
        >
          <div className="flex justify-between items-center px-4 h-14 border-b border-white/10">
            <div className="text-sm font-semibold text-white">{title}</div>
            <button className="text-white/80" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div className="p-4 overflow-y-auto h-[calc(100vh-56px)]">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex relative bg-black text-dex-text-primary">
      {/* ===== Desktop Sidebar (fixed rail) ===== */}
      <aside
        className={[
          "relative overflow-hidden z-10",
          "hidden lg:flex flex-col border-r border-white/10",
          "lg:fixed lg:top-0 lg:bottom-0 lg:left-0 lg:w-16",
          "transition-all duration-200 ease-in-out",
        ].join(" ")}
        style={{ backgroundColor: "#000" }}
      >
        <div className="pointer-events-none absolute inset-0 bg-white/[0.02]" />
        <div className="flex relative z-10 flex-col h-full">
          {/* Rail header */}
          <div className="flex justify-center items-center h-14">
            <NavLink to="/" className="grid place-items-center">
              <img
                src="/images/logo.png"
                alt="CryptoScan"
                className="w-10 h-10 rounded-2xl ring-1 ring-white/20 bg-white/[0.03] object-cover transition-opacity duration-150 hover:opacity-90"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </NavLink>
          </div>

          {/* Rail content */}
          <div className="overflow-y-auto flex-1 px-1 min-h-0">
            <div className="mt-2">
              <RailSection items={mainNavItems} />
            </div>
            <div className="my-2 h-px bg-white/10" />
            <RailButton
              label="Launchpad"
              Icon={FaBolt}
              onClick={() => setOpenRailLaunchpad(true)}
            />
            <RailButton
              label="More"
              Icon={FaTh}
              onClick={() => setOpenRailMore(true)}
            />
          </div>

          {/* Footer: user / auth in rail */}
          <div className="p-3 border-t border-white/10">
            {user ? (
              <div className="flex flex-col gap-2 items-center">
                <div
                  onClick={() => navigate("/profile")}
                  className="flex relative justify-center items-center w-8 h-8 font-bold text-white rounded-full ring-1 cursor-pointer bg-white/10 ring-white/10 hover:ring-white/30"
                  title="Open Profile"
                >
                  {(userDisplay || user.email || "?")[0].toUpperCase()}
                </div>
                <button
                  onClick={logout}
                  className="px-2.5 py-1 text-[11px] rounded-lg bg-white/10 hover:bg-white/15 text-rose-200"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                className="grid place-items-center w-12 h-12 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] mx-auto"
                title="Login / Register"
              >
                <span className="text-[11px] text-white/80">Auth</span>
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* ===== Main column (offset by fixed rail) ===== */}
      <div className="relative w-full min-w-0 lg:ml-16 home-content-wrapper">
        {/* Page content */}
        <main className="block" style={{ overflowX: "visible" }}>
          {children}
        </main>
      </div>

      {/* ---------- DESKTOP RAIL DRAWERS ---------- */
      /* Launchpad */}
      <RailDrawer
        open={openRailLaunchpad}
        title="Launchpads"
        onClose={() => setOpenRailLaunchpad(false)}
      >
        <div className="grid grid-cols-2 gap-3">
          {launchpadNavItems.map(({ to, label, icon: Icon }) => (
            <DrawerTile
              key={to}
              to={to}
              label={label}
              Icon={Icon}
              onClick={() => setOpenRailLaunchpad(false)}
            />
          ))}
        </div>
      </RailDrawer>

      {/* More */}
      <RailDrawer
        open={openRailMore}
        title="More"
        onClose={() => setOpenRailMore(false)}
      >
        <div className="grid grid-cols-2 gap-3">
          {moreNavItems.map(({ to, label, icon: Icon }) => (
            <DrawerTile
              key={to}
              to={to}
              label={label}
              Icon={Icon}
              onClick={() => setOpenRailMore(false)}
            />
          ))}
        </div>
      </RailDrawer>

      {/* ---------- MOBILE: Sticky Bottom Navigation ---------- */}
      <div
        className="fixed right-0 bottom-0 left-0 z-50 border-t backdrop-blur-md lg:hidden border-white/10 bg-black/80"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex justify-between items-center px-3 py-2">
          <button
            onClick={() => navigate("/")}
            className="flex flex-col items-center gap-1 text-[11px] text-white/85 active:opacity-80"
            aria-label="Home"
          >
            <img
              src="/images/logo.png"
              alt="Home"
              className="rounded ring-1 ring-white/10"
              style={{
                width: MOBILE_HOME_LOGO_PX,
                height: MOBILE_HOME_LOGO_PX,
              }}
            />
            <span>Home</span>
          </button>

          <button
            onClick={() => setMobileSearchOpen(true)}
            className="flex flex-col items-center gap-1 text-[11px] text-white/85 active:opacity-80"
            aria-label="Search"
          >
            <FaSearch className={MOBILE_ICON_CLASS} />
            <span>Search</span>
          </button>

          {/* Only show this on token pages */}
          {isTokenPage && (
            <button
              onClick={() => setMobileMode((m) => (m === "info" ? "chart" : "info"))}
              className="flex flex-col items-center gap-1 text-[11px] text-white/85 active:opacity-80"
              aria-label="Chart Toggle"
            >
              <FaChartLine className={MOBILE_ICON_CLASS} />
              <span>{mobileMode === "info" ? "Open Chart" : "Back to Info"}</span>
            </button>
          )}

          <button
            onClick={() => navigate("/portfolio")}
            className="flex flex-col items-center gap-1 text-[11px] text-white/85 active:opacity-80"
            aria-label="Watchlist"
          >
            <FaStar className={MOBILE_ICON_CLASS} />
            <span>Watchlist</span>
          </button>

          <button
            onClick={() => navigate("/alerts")}
            className="flex flex-col items-center gap-1 text-[11px] text-white/85 active:opacity-80"
            aria-label="Alerts"
          >
            <FaBell className={MOBILE_ICON_CLASS} />
            <span>Alerts</span>
          </button>

          <button
            onClick={() => setMobileOpen(true)}
            className="flex flex-col items-center gap-1 text-[11px] text-white/85 active:opacity-80"
            aria-label="Menu"
          >
            <FaBars className={MOBILE_ICON_CLASS} />
            <span>Menu</span>
          </button>
        </div>
      </div>

      {/* ---------- MOBILE: Search Bottom Sheet ---------- */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileSearchOpen(false)}
          />
          <div
            className="absolute right-0 bottom-0 left-0 p-4 bg-black rounded-t-2xl border-t border-white/10"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <div className="mx-auto mb-2 w-12 h-1.5 rounded-full bg-white/20" />
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm font-semibold text-white">Search</div>
              <button
                className="text-white/80"
                onClick={() => setMobileSearchOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search token, address…"
                className="px-8 py-2 w-full text-sm placeholder-gray-400 text-white bg-gray-800 rounded outline-none"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGlobalSearch()}
                autoFocus
              />
              <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-white/50" />
              <button
                className="absolute right-1 top-1/2 px-2 py-1 text-xs rounded -translate-y-1/2 btn"
                onClick={handleGlobalSearch}
              >
                Go
              </button>
            </div>

            {recentSearches.length > 0 && (
              <div className="mt-3">
                <div className="mb-1 text-xs text-white/60">Recent</div>
                <div className="flex flex-wrap gap-1">
                  {recentSearches.map((s, i) => (
                    <button
                      key={i}
                      title={s}
                      onClick={() => {
                        setSearchValue(s);
                        handleGlobalSearch();
                      }}
                      className="px-2 py-1 text-xs rounded border bg-white/5 border-white/10 text-white/80 hover:text-white"
                    >
                      {s.length > 18 ? `${s.slice(0, 16)}…` : s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- MOBILE: Bottom Drawer Menu ---------- */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[55] lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="absolute right-0 bottom-0 left-0 pt-2 bg-black rounded-t-2xl border-t border-white/10"
            style={{
              maxHeight: "80vh",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
          >
            <div
              className="mx-auto mb-2 w-12 h-1.5 rounded-full bg-white/20"
              onClick={() => setMobileOpen(false)}
            />
            <div className="flex justify-between items-center px-4 pb-2">
              <div className="text-sm font-semibold text-white">Menu</div>
              <button
                className="text-white/80"
                onClick={() => setMobileOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div
              className="overflow-y-auto px-4 pb-4"
              style={{ maxHeight: "calc(80vh - 56px)" }}
            >
              {/* Drawer search */}
              <div className="flex gap-3 mb-4">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    placeholder="Search token, address…"
                    className="px-8 py-2 w-full text-sm placeholder-gray-400 text-white bg-gray-800 rounded"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleGlobalSearch()}
                  />
                  <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-white/50" />
                  <button
                    className="absolute right-1 top-1/2 px-2 py-1 text-xs rounded -translate-y-1/2 btn"
                    onClick={handleGlobalSearch}
                  >
                    Go
                  </button>
                </div>
                {/* Auth shortcut */}
                <div className="block">
                  {user ? (
                    <button
                      onClick={() => {
                        logout();
                        setMobileOpen(false);
                      }}
                      className="px-3 py-2 w-full text-sm text-white rounded bg-red-500/20 hover:bg-red-500/30"
                    >
                      Logout
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setShowAuth(true);
                        setMobileOpen(false);
                      }}
                      className="px-3 py-2 w-full text-sm text-white rounded btn"
                    >
                      🔐 Login / Register
                    </button>
                  )}
                </div>
              </div>

              {/* Explore */}
              <div className="mb-5">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-white/50">
                  Explore
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {mainNavItems.map(({ to, label, icon: Icon }) => (
                    <DrawerTile
                      key={to}
                      to={to}
                      label={label}
                      Icon={Icon}
                      onClick={() => setMobileOpen(false)}
                    />
                  ))}
                </div>
              </div>

              {/* Launchpads */}
              <div className="mb-5">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-white/50">
                  Launchpads
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {launchpadNavItems.map(({ to, label, icon: Icon }) => (
                    <DrawerTile
                      key={to}
                      to={to}
                      label={label}
                      Icon={Icon}
                      onClick={() => setMobileOpen(false)}
                    />
                  ))}
                </div>
              </div>

              {/* More */}
              <div className="mb-2">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-white/50">
                  More
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {moreNavItems.map(({ to, label, icon: Icon }) => (
                    <DrawerTile
                      key={to}
                      to={to}
                      label={label}
                      Icon={Icon}
                      onClick={() => setMobileOpen(false)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default MainLayout;
